import React, { useEffect, useState } from "react";
import { api, formatMoney, formatApiError } from "../../lib/api";
import { BRANDS, darkLogoFor } from "../../lib/brand";
import { Plus, Trash2, Search, X, Edit2, PackagePlus, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 50;

// Brand options for the manual product form (kept locally so this page is
// self-contained — mirrors the list in AdminProducts.js).
const MANUAL_BRAND_OPTIONS = [
  { value: "", label: "— Nincs / Általános —" },
  { value: "audi", label: "Audi" },
  { value: "vw", label: "Volkswagen" },
  { value: "skoda", label: "Škoda" },
  { value: "seat", label: "SEAT" },
  { value: "bmw", label: "BMW" },
  { value: "mercedes", label: "Mercedes-Benz" },
  { value: "hyundai", label: "Hyundai" },
  { value: "kia", label: "Kia" },
  { value: "toyota", label: "Toyota" },
  { value: "ford", label: "Ford" },
  { value: "opel", label: "Opel" },
  { value: "renault", label: "Renault" },
  { value: "peugeot", label: "Peugeot" },
  { value: "citroen", label: "Citroën" },
  { value: "fiat", label: "Fiat" },
  { value: "nissan", label: "Nissan" },
  { value: "mazda", label: "Mazda" },
  { value: "suzuki", label: "Suzuki" },
  { value: "honda", label: "Honda" },
  { value: "__custom__", label: "Egyéb (saját)..." },
];

const FIELD_LABELS = {
  sku: "Cikkszám", name: "Megnevezés", category: "Kategória",
  image_url: "Kép URL", stock: "Készlet", base_price: "Alapár (nettó)", description: "Leírás",
  brand: "Márka",
};

export default function AdminManualProducts() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [brand, setBrand] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const id = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => { setPage(0); }, [qDebounced, brand]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: p } = await api.get("/products", {
        params: {
          source: "manual",
          q: qDebounced || undefined,
          brand: brand || undefined,
          skip: page * PAGE_SIZE,
          limit: PAGE_SIZE,
        },
      });
      setData(p);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDebounced, brand, page]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

  const deleteProduct = async (id, sku) => {
    if (!window.confirm(`Biztosan törli a(z) "${sku}" manuális terméket?`)) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Manuális termék törölve");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Build a unique brand list from the loaded manual products for the filter dropdown.
  const distinctBrands = Array.from(
    new Set(data.items.map((p) => p.brand).filter(Boolean)),
  ).sort();

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-2">
        <div>
          <h1 className="text-2xl font-bold uppercase tracking-wide flex items-center gap-2">
            <PackagePlus size={22} /> Manuális termékek
          </h1>
          <p className="text-sm" style={{ color: "var(--text-mute)" }}>
            Kézzel felvitt cikkszámok (nem CSV/XLSX feltöltésből) —
            <strong style={{ color: "var(--brand)" }}> {data.total.toLocaleString("hu-HU")}</strong> darab
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowBulk((s) => !s); setShowCreate(false); setEditing(null); }}
            className="btn-ghost"
            data-testid="manual-toggle-bulk"
          >
            <FileText size={12} /> Tömeges felvitel
          </button>
          <button
            onClick={() => { setEditing(null); setShowCreate(true); setShowBulk(false); }}
            className="btn-yellow"
            data-testid="manual-add-product"
          >
            <Plus size={12} /> Új termék felvitele
          </button>
        </div>
      </div>
      <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

      <div className="card-flat p-4 mb-4 text-sm" style={{ background: "rgba(15,118,110,0.05)", border: "1px solid #0f766e" }}>
        <div className="font-bold uppercase mb-1" style={{ color: "#0f766e" }}>
          ℹ️ Mire jó ez az oldal?
        </div>
        <div style={{ color: "var(--text)" }}>
          Itt vihetsz fel olyan cikkszámokat, amelyek <strong>egyik CSV/XLSX listában sem szerepelnek</strong> (pl. Hyundai, Kia, Toyota,
          egyedi alkatrészek). A megadott <strong>alapárra a vevő márka-árrése (markup)</strong> automatikusan rárakódik
          — pont úgy, mint a normál CSV-ből betöltött termékeknél. A manuális termékek a fő katalógusban is megjelennek,
          és a globális keresőben is megtalálhatóak.
        </div>
      </div>

      {(showCreate || editing) && (
        <ManualProductForm
          initial={editing}
          onDone={() => { setShowCreate(false); setEditing(null); fetchData(); }}
          onClose={() => { setShowCreate(false); setEditing(null); }}
        />
      )}

      {showBulk && (
        <BulkImportForm
          onDone={() => { setShowBulk(false); fetchData(); }}
          onClose={() => setShowBulk(false)}
        />
      )}

      <div className="card-flat p-3 mb-3 flex items-center gap-2 flex-wrap">
        <Search size={14} style={{ color: "var(--text-mute)" }} />
        <input
          className="input !py-1.5 flex-1 min-w-[200px]"
          placeholder="Cikkszám vagy megnevezés keresése..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="manual-products-search"
        />
        {q && <button onClick={() => setQ("")} style={{ color: "var(--text-mute)" }}><X size={14} /></button>}
        <select
          className="input !py-1.5 !w-auto text-xs"
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          data-testid="manual-products-brand-filter"
        >
          <option value="">— Minden márka —</option>
          {distinctBrands.map((b) => (
            <option key={b} value={b}>
              {BRANDS[b]?.label || b.charAt(0).toUpperCase() + b.slice(1)}
            </option>
          ))}
        </select>
      </div>

      <div className="card-elev overflow-hidden">
        <div className="section-title">Manuális termékek listája</div>
        <div className="overflow-x-auto">
          <table className="table-b2b" data-testid="manual-products-table">
            <thead>
              <tr>
                <th>Cikkszám</th>
                <th>Márka</th>
                <th>Megnevezés</th>
                <th style={{ width: 80 }}>Készlet</th>
                <th style={{ width: 110 }} className="text-right">Alapár</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--text-mute)" }}>Betöltés...</td></tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12" style={{ color: "var(--text-mute)" }}>
                    <PackagePlus size={36} className="mx-auto mb-3 opacity-40" />
                    Még nincs manuálisan felvitt termék. Nyomd meg az „Új termék felvitele&rdquo; gombot fent.
                  </td>
                </tr>
              ) : (
                data.items.map((p) => {
                  const brandObj = p.brand && BRANDS[p.brand];
                  return (
                    <tr key={p.id} data-testid={`manual-product-row-${p.sku}`}>
                      <td>
                        <span className="font-mono text-xs font-bold" style={{ color: "var(--brand)" }}>{p.sku}</span>
                      </td>
                      <td>
                        {brandObj ? (
                          <span className="inline-flex items-center gap-1.5">
                            <img src={darkLogoFor(brandObj)} alt={brandObj.label} className="h-3.5 w-3.5 object-contain" />
                            <span className="text-xs uppercase">{brandObj.label}</span>
                          </span>
                        ) : p.brand ? (
                          <span className="text-xs uppercase">{p.brand}</span>
                        ) : <span style={{ color: "var(--text-disabled)" }}>—</span>}
                      </td>
                      <td className="font-bold">{p.name}</td>
                      <td className="font-mono">{p.stock}</td>
                      <td className="text-right font-mono">
                        {p.base_price === 0 ? (
                          <span style={{ color: "var(--text-disabled)" }}>—</span>
                        ) : (
                          formatMoney(p.base_price)
                        )}
                      </td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          <button
                            onClick={() => { setShowCreate(false); setEditing(p); }}
                            className="btn-ghost !py-1 !px-2 text-xs"
                            title="Szerkesztés"
                            data-testid={`manual-edit-${p.sku}`}
                          >
                            <Edit2 size={12} />
                          </button>
                          <button
                            onClick={() => deleteProduct(p.id, p.sku)}
                            className="btn-danger !py-1 !px-2 text-xs"
                            data-testid={`manual-delete-${p.sku}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {totalPages > 1 && (
        <div className="card-flat p-3 mt-3 flex items-center justify-between">
          <div className="text-xs" style={{ color: "var(--text-mute)" }}>
            <strong>{page + 1}</strong>. oldal / {totalPages.toLocaleString("hu-HU")}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="btn-ghost !py-1 !px-2 text-[11px] disabled:opacity-30">Előző</button>
            <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="btn-primary !py-1 !px-2 text-[11px] disabled:opacity-30">Következő</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ManualProductForm({ initial, onDone, onClose }) {
  const isEdit = !!initial?.id;
  const initialBrand = (initial?.brand || "").toLowerCase();
  const initialBrandKnown = MANUAL_BRAND_OPTIONS.some(
    (o) => o.value && o.value !== "__custom__" && o.value === initialBrand,
  );
  const [form, setForm] = useState({
    sku: initial?.sku || "",
    name: initial?.name || "",
    description: initial?.description || "",
    category: initial?.category || "",
    brand: initialBrand,
    stock: initial?.stock ?? 0,
    base_price: initial?.base_price ?? 0,
    image_url: initial?.image_url || "",
  });
  const [brandMode, setBrandMode] = useState(
    initialBrand && !initialBrandKnown ? "__custom__" : initialBrand || "",
  );
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        brand: (form.brand || "").trim().toLowerCase(),
        stock: Number(form.stock),
        base_price: Number(form.base_price),
        group_prices: initial?.group_prices || {},
      };
      if (isEdit) {
        await api.put(`/admin/products/${initial.id}`, payload);
        toast.success("Manuális termék frissítve");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Manuális termék létrehozva");
      }
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-elev mb-5" data-testid="manual-product-form">
      <div className="section-title justify-between">
        <span>{isEdit ? `Manuális termék szerkesztése — ${initial.sku}` : "Új manuális termék felvitele"}</span>
        <button type="button" onClick={onClose} style={{ color: "#fff" }}><X size={16} /></button>
      </div>
      <div className="p-5 grid sm:grid-cols-2 gap-3">
        {["sku", "name", "category", "image_url"].map((k) => (
          <div key={k}>
            <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS[k]}</label>
            <input
              className="input"
              required={k === "sku" || k === "name"}
              disabled={isEdit && k === "sku"}
              value={form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              data-testid={`manual-form-${k}`}
            />
          </div>
        ))}
        <div>
          <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS.brand}</label>
          <select
            className="input"
            value={brandMode}
            onChange={(e) => {
              const v = e.target.value;
              setBrandMode(v);
              if (v !== "__custom__") {
                setForm({ ...form, brand: v });
              } else {
                setForm({ ...form, brand: "" });
              }
            }}
            data-testid="manual-form-brand-select"
          >
            {MANUAL_BRAND_OPTIONS.map((o) => (
              <option key={o.value || "_none"} value={o.value}>{o.label}</option>
            ))}
          </select>
          {brandMode === "__custom__" && (
            <input
              className="input mt-2"
              placeholder="Pl. hyundai, kia, toyota..."
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
              data-testid="manual-form-brand-custom"
            />
          )}
          <div className="text-[10px] mt-1" style={{ color: "var(--text-mute)" }}>
            A vevő márka-árrése automatikusan rárakódik.
          </div>
        </div>
        <div>
          <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS.stock}</label>
          <input type="number" className="input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="manual-form-stock" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS.base_price}</label>
          <input type="number" step="0.01" className="input" required value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} data-testid="manual-form-price" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-bold uppercase block mb-1">{FIELD_LABELS.description}</label>
          <textarea className="input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="manual-form-description" />
        </div>
      </div>
      <div className="px-5 pb-5 flex gap-2">
        <button type="submit" disabled={saving} className="btn-primary" data-testid="manual-form-save">
          {saving ? "Mentés..." : isEdit ? "Módosítások mentése" : "Termék mentése"}
        </button>
        <button type="button" onClick={onClose} className="btn-ghost">Mégse</button>
      </div>
    </form>
  );
}


// --------------------------------------------------------------------------
// Bulk import — paste-in semicolon CSV:
//   SKU;Megnevezés;Márka;Készlet;Ár;Leírás
// Up to ~500 rows handled sequentially client-side. Each row hits
// POST /admin/products one at a time; success/error tally shown per row.
// --------------------------------------------------------------------------

const SAMPLE_CSV = `HYU-2030;Hyundai i30 1.6 CRDi olajszűrő;hyundai;15;7.90;OEM minőség
HYU-2031;Hyundai Tucson levegőszűrő;hyundai;8;12.50;
KIA-105;Kia Ceed fékbetét készlet első;kia;6;28.00;`;

function BulkImportForm({ onDone, onClose }) {
  const [text, setText] = useState("");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);

  const parseRow = (line) => {
    const parts = line.split(";").map((s) => s.trim());
    if (parts.length < 5) {
      return { error: `Túl kevés mező (${parts.length}/5+) — szükséges: SKU;Név;Márka;Készlet;Ár[;Leírás]` };
    }
    const [sku, name, brand, stock, price, ...rest] = parts;
    const description = rest.join(";").trim();
    if (!sku) return { error: "Üres SKU" };
    if (!name) return { error: "Üres megnevezés" };
    const stockN = Number((stock || "0").replace(/,/g, "."));
    const priceN = Number((price || "0").replace(/,/g, "."));
    if (!Number.isFinite(priceN) || priceN <= 0) {
      return { error: `Érvénytelen ár: "${price}"` };
    }
    if (!Number.isFinite(stockN) || stockN < 0) {
      return { error: `Érvénytelen készlet: "${stock}"` };
    }
    return {
      payload: {
        sku, name,
        brand: (brand || "").toLowerCase(),
        stock: Math.floor(stockN),
        base_price: priceN,
        description,
        category: "",
        image_url: "",
        group_prices: {},
      },
    };
  };

  const run = async () => {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l && !l.startsWith("#"));
    if (lines.length === 0) {
      toast.error("Üres a beillesztett szöveg.");
      return;
    }
    if (lines.length > 500) {
      toast.error(`Túl sok sor (${lines.length}). Max 500 / kör — bontsd több részletre.`);
      return;
    }
    setRunning(true);
    const errors = [];
    let ok = 0;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const parsed = parseRow(line);
      if (parsed.error) {
        errors.push({ line: i + 1, sku: line.split(";")[0] || "?", reason: parsed.error });
        continue;
      }
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post("/admin/products", parsed.payload);
        ok += 1;
      } catch (e) {
        errors.push({ line: i + 1, sku: parsed.payload.sku, reason: formatApiError(e) });
      }
    }
    setResults({ ok, fail: errors.length, errors });
    setRunning(false);
    if (ok > 0) {
      toast.success(`${ok} termék felvitelre került${errors.length ? `, ${errors.length} sor hibás` : ""}.`);
    } else {
      toast.error("Egyik sort sem sikerült felvinni — lásd a hibalistát.");
    }
  };

  const validLineCount = text.split(/\r?\n/).filter((l) => l.trim() && !l.trim().startsWith("#")).length;

  return (
    <div className="card-elev mb-5" data-testid="bulk-import-form">
      <div className="section-title justify-between" style={{ background: "linear-gradient(180deg, #0f766e 0%, #134e4a 100%)" }}>
        <span className="flex items-center gap-2"><FileText size={14} /> Tömeges felvitel — beilleszthető CSV</span>
        <button type="button" onClick={onClose} style={{ color: "#fff" }}><X size={16} /></button>
      </div>
      <div className="p-5 space-y-3 text-sm">
        <div>
          <div className="text-xs font-bold uppercase mb-1">Formátum (pontosvesszővel)</div>
          <code className="block font-mono text-xs p-2 rounded" style={{ background: "var(--surface-elev)", border: "1px solid var(--border)" }}>
            SKU;Megnevezés;Márka;Készlet;Ár;Leírás (opc.)
          </code>
          <ul className="text-[11px] mt-2 space-y-0.5" style={{ color: "var(--text-mute)" }}>
            <li>&bull; Egy termék = egy sor. Üres sorok és <code className="font-mono">#</code>-kel kezdődő megjegyzések kihagyva.</li>
            <li>&bull; <strong>Márka</strong>: kisbetűs azonosító (pl. <code className="font-mono">hyundai</code>, <code className="font-mono">kia</code>, <code className="font-mono">audi</code>). Lehet üres is.</li>
            <li>&bull; <strong>Ár</strong>: EUR nettó, vessző vagy pont decimális (pl. <code className="font-mono">12,50</code> vagy <code className="font-mono">12.50</code>).</li>
            <li>&bull; A vevő márka-árrése (markup) ezekre is automatikusan rárakódik.</li>
            <li>&bull; Max <strong>500 sor / kör</strong>. Hibás sorokat átugorjuk, listát kapsz a végén.</li>
          </ul>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-bold uppercase">CSV sorok</label>
            <button
              type="button"
              onClick={() => setText(SAMPLE_CSV)}
              className="btn-ghost !py-1 !px-2 text-[10px]"
              data-testid="bulk-fill-sample"
            >
              Minta beillesztése
            </button>
          </div>
          <textarea
            className="input font-mono text-xs"
            rows={10}
            placeholder={"HYU-2030;Hyundai i30 1.6 CRDi olajszűrő;hyundai;15;7.90;OEM minőség\nKIA-105;Kia Ceed fékbetét készlet első;kia;6;28.00;"}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={running}
            data-testid="bulk-import-textarea"
          />
          <div className="text-[10px] mt-1" style={{ color: "var(--text-mute)" }}>
            {validLineCount} érvényes sor
          </div>
        </div>

        {results && (
          <div className="space-y-2" data-testid="bulk-results">
            <div className="grid grid-cols-2 gap-2">
              <div className="card-flat p-2 text-center" style={{ background: "#E6F4EA", border: "1px solid #0A8043" }}>
                <div className="text-[10px] uppercase font-bold" style={{ color: "#0A8043" }}>Sikeres</div>
                <div className="text-2xl font-bold" style={{ color: "#0A8043" }} data-testid="bulk-ok">{results.ok}</div>
              </div>
              <div className="card-flat p-2 text-center" style={{ background: "#FDECEC", border: "1px solid #c8102e" }}>
                <div className="text-[10px] uppercase font-bold" style={{ color: "#c8102e" }}>Hibás</div>
                <div className="text-2xl font-bold" style={{ color: "#c8102e" }} data-testid="bulk-fail">{results.fail}</div>
              </div>
            </div>
            {results.errors.length > 0 && (
              <div className="max-h-48 overflow-y-auto text-xs border rounded" style={{ borderColor: "var(--border)" }} data-testid="bulk-errors">
                <table className="table-b2b">
                  <thead><tr><th>Sor</th><th>SKU</th><th>Hiba</th></tr></thead>
                  <tbody>
                    {results.errors.map((er, i) => (
                      <tr key={i}>
                        <td className="font-mono">{er.line}</td>
                        <td className="font-mono">{er.sku}</td>
                        <td style={{ color: "#c8102e" }}>{er.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={run}
            disabled={running || !text.trim()}
            className="btn-primary"
            data-testid="bulk-import-run"
          >
            {running ? (<><Loader2 size={12} className="animate-spin" /> Felvitel...</>) : ("Felvitel indítása")}
          </button>
          {results && (
            <button
              type="button"
              onClick={() => { setText(""); setResults(null); onDone(); }}
              className="btn-ghost"
              data-testid="bulk-import-done"
            >
              Kész — lista frissítése
            </button>
          )}
          <button type="button" onClick={onClose} className="btn-ghost">Bezár</button>
        </div>
      </div>
    </div>
  );
}
